import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { useCopy, useLang } from "@/contexts/LanguageContext";

const COPY = {
  en: { h: "Analytics", p: "District, sector and funnel intelligence.", d: "District demand (top)", s: "Sector demand", f: "Application funnel" },
  hi: { h: "विश्लेषण", p: "जिला, क्षेत्र और फ़नल संबंधी जानकारी।", d: "जिलेवार माँग (शीर्ष)", s: "क्षेत्रवार माँग", f: "आवेदन फ़नल" },
};

const COLORS = ["#0f172a", "#ea580c", "#10b981", "#0ea5e9", "#a855f7", "#f59e0b", "#ef4444", "#14b8a6"];

export default function Analytics() {
  const [district, setDistrict] = useState([]);
  const [sector, setSector] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const c = useCopy(COPY);
  const { tv } = useLang();
  const sectorData = sector.slice(0, 8).map((s) => ({ ...s, sector: tv(s.sector) }));
  const funnelData = funnel.map((f) => ({ ...f, status: tv(f.status) }));
  useEffect(() => {
    api.get("/reports/district-demand").then((r) => setDistrict(r.data)).catch(() => {});
    api.get("/reports/sector-demand").then((r) => setSector(r.data)).catch(() => {});
    api.get("/reports/application-funnel").then((r) => setFunnel(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-6" data-testid="bank-analytics">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 brand-heading">{c.h}</h1>
        <p className="text-slate-600 mt-1 text-sm">{c.p}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-6 rounded-lg border border-slate-200 bg-white">
          <h2 className="font-semibold text-slate-900 brand-heading mb-3">{c.d}</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={district.slice(0, 10)}>
              <XAxis dataKey="district" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#0f172a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="p-6 rounded-lg border border-slate-200 bg-white">
          <h2 className="font-semibold text-slate-900 brand-heading mb-3">{c.s}</h2>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={sectorData} dataKey="count" nameKey="sector" outerRadius={90} label>
                {sector.slice(0, 8).map((s, i) => <Cell key={s.sector || `bar-${i}`} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="p-6 rounded-lg border border-slate-200 bg-white lg:col-span-2">
          <h2 className="font-semibold text-slate-900 brand-heading mb-3">{c.f}</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funnelData} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="status" width={200} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#ea580c" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <DisclaimerBanner compact />
    </div>
  );
}
